import { runWithLogger } from "@dailywars/core/log";
import { isNotFound, isRedirect } from "@tanstack/react-router";
import { createMiddleware } from "@tanstack/react-start";
import { createError, EvlogError } from "evlog";

import { getRequestLogger } from "#/lib/requestLogger.server";

function isEvlogError(error: unknown): error is EvlogError {
  return (
    error instanceof EvlogError ||
    (typeof error === "object" && error !== null && (error as Error).name === "EvlogError")
  );
}

function logRequestErrorSafe(error: unknown): void {
  try {
    getRequestLogger().error(error instanceof Error ? error : new Error(String(error)));
  } catch {}
}

function logEvlogErrorIfServerFailure(error: EvlogError): void {
  if (error.status < 500) return;
  logRequestErrorSafe(error);
}

function throwStructuredServerError(error: unknown): never {
  logRequestErrorSafe(error);
  throw createError({
    message: "Something went wrong",
    status: 500,
    why: error instanceof Error ? error.message : "An unexpected error occurred.",
    fix: "Try again in a few moments.",
    cause: error instanceof Error ? error : undefined,
    internal: { source: "server_fn" },
  }).toJSON();
}

export const evlogServerFnErrorMiddleware = createMiddleware({ type: "function" }).server(
  async ({ next }) => {
    const handle = async () => {
      try {
        return await next();
      } catch (error: unknown) {
        if (isEvlogError(error)) {
          logEvlogErrorIfServerFailure(error);
          throw error.toJSON();
        }
        if (isRedirect(error) || isNotFound(error)) {
          throw error;
        }
        throwStructuredServerError(error);
      }
    };

    try {
      const requestLog = getRequestLogger();
      return runWithLogger(requestLog, handle);
    } catch {
      return handle();
    }
  },
);
